/**
 * Project + permission schemas (Phase 5 / M2 + M3).
 *
 * Mirrors:
 *   - chaos.projects                (Alembic 024)
 *   - chaos.project_members         (Alembic 024)
 *   - chaos.board_members           (Alembic 004)
 *   - chaos.column_watchers         (Alembic 004)
 *   - chaos.card_invitees           (Alembic 004)
 *   - chaos.invitations             (Alembic 004)
 *
 * Resolution algorithm lives in apps/chaos-gateway/src/lib/permissions.ts
 * and follows docs/PERMISSIONS_MODEL.md §6.
 */
import { z } from 'zod';

// ── Project membership roles ─────────────────────────────────────────
// Per docs/PERMISSIONS_MODEL.md §3 — owner/editor/commenter/viewer.
// commenter = read + comment (not implemented as a write tier until
// chat comments land), viewer = read-only.
export const projectMemberRoles = ['owner', 'editor', 'commenter', 'viewer'] as const;
export const projectMemberRoleSchema = z.enum(projectMemberRoles);
export type ProjectMemberRole = z.infer<typeof projectMemberRoleSchema>;

// ── Board membership levels ──────────────────────────────────────────
// Mirrors chaos.board_members.permission_level CHECK constraint.
export const boardMemberLevels = ['read', 'edit', 'admin'] as const;
export const boardMemberLevelSchema = z.enum(boardMemberLevels);
export type BoardMemberLevel = z.infer<typeof boardMemberLevelSchema>;

// ── Card invitee roles ───────────────────────────────────────────────
// Mirrors chaos.card_invitees.invitee_role CHECK constraint. The Phase 5
// UI exposes editor/commenter/viewer as a friendlier subset; the
// backend keeps the full enum for column-watcher auto-invitations.
export const cardInviteeRoles = ['hitl', 'controller', 'participant', 'viewer', 'auditor'] as const;
export const cardInviteeRoleSchema = z.enum(cardInviteeRoles);
export type CardInviteeRole = z.infer<typeof cardInviteeRoleSchema>;

// User-facing simplified invitation roles (Phase 5 UI surface).
export const inviteRoles = ['editor', 'commenter', 'viewer'] as const;
export const inviteRoleSchema = z.enum(inviteRoles);
export type InviteRole = z.infer<typeof inviteRoleSchema>;

// ── Project ──────────────────────────────────────────────────────────
export const projectSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  name: z.string().min(1).max(120),
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase letters, digits, single hyphen separators'),
  description: z.string().nullable(),
  color: z.string().nullable(),
  icon: z.string().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  archivedAt: z.string().datetime().nullable(),
});
export type Project = z.infer<typeof projectSchema>;

export const createProjectSchema = z.object({
  name: z.string().min(1).max(120),
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'lowercase letters, digits, single hyphen separators')
    .optional(),
  description: z.string().max(2000).nullable().optional(),
  color: z.string().max(32).nullable().optional(),
  icon: z.string().max(32).nullable().optional(),
});
export type CreateProjectInput = z.infer<typeof createProjectSchema>;

export const updateProjectSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).nullable().optional(),
  color: z.string().max(32).nullable().optional(),
  icon: z.string().max(32).nullable().optional(),
});
export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

// ── Project member ──────────────────────────────────────────────────
export const projectMemberSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  userId: z.string().uuid(),
  role: projectMemberRoleSchema,
  addedBy: z.string().uuid().nullable(),
  addedAt: z.string().datetime(),
  removedAt: z.string().datetime().nullable(),
});
export type ProjectMember = z.infer<typeof projectMemberSchema>;

// User context joined onto the membership for UI rendering.
export const projectMemberWithUserSchema = projectMemberSchema.extend({
  email: z.string().email(),
  displayName: z.string().nullable(),
});
export type ProjectMemberWithUser = z.infer<typeof projectMemberWithUserSchema>;

export const addProjectMemberSchema = z.object({
  userId: z.string().uuid(),
  role: projectMemberRoleSchema,
});
export type AddProjectMemberInput = z.infer<typeof addProjectMemberSchema>;

export const updateProjectMemberSchema = z.object({
  role: projectMemberRoleSchema,
});
export type UpdateProjectMemberInput = z.infer<typeof updateProjectMemberSchema>;

// ── Card invitation (Phase 5 surface) ────────────────────────────────
// Backed by chaos.card_invitees. The Phase 5 UI exposes the three
// editor/commenter/viewer roles directly; the gateway translates them
// onto the underlying invitee_role.
export const cardInvitationSchema = z.object({
  id: z.string().uuid(),
  cardId: z.string().uuid(),
  userId: z.string().uuid(),
  role: cardInviteeRoleSchema,
  invitedBy: z.string().uuid().nullable(),
  invitedAt: z.string().datetime(),
  expiresAt: z.string().datetime().nullable(),
  removedAt: z.string().datetime().nullable(),
  source: z.string().nullable(),
});
export type CardInvitation = z.infer<typeof cardInvitationSchema>;

export const cardInvitationWithUserSchema = cardInvitationSchema.extend({
  email: z.string().email(),
  displayName: z.string().nullable(),
});
export type CardInvitationWithUser = z.infer<typeof cardInvitationWithUserSchema>;

export const inviteToCardSchema = z.object({
  userId: z.string().uuid(),
  role: inviteRoleSchema,
});
export type InviteToCardInput = z.infer<typeof inviteToCardSchema>;

// ── Workspace member helpers (Phase 5 settings surface) ─────────────
// Lightweight projection of auth.workspace_memberships joined with
// auth.users — the data the Settings → Members tab renders.
export const workspaceMemberSummarySchema = z.object({
  userId: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string().nullable(),
  role: z.string(),
  joinedAt: z.string().datetime(),
});
export type WorkspaceMemberSummary = z.infer<typeof workspaceMemberSummarySchema>;
